import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import { useMap } from "vue-maplibre-gl";
import type { GeoJSONSource, Map, MapMouseEvent } from "maplibre-gl";
import type { GeoJSON } from "geojson";

import { circleRing, vertexPolygon, type DrawMode } from "@/map/drawModes";

/**
 * Shared map drawing primitives for every draw-enabled tool: a temporary
 * GeoJSON source with fill+line layers, an overlay that renders the in-progress
 * shape plus the committed polygons, cursor/pan handling while armed, and a
 * user-selectable MODE that decides how a shape is produced — a sampled
 * freehand drag, a circle sized from its centre (approximated as a polygon, see
 * @/map/drawModes), or a clicked-vertex ring closed by double-click or Enter.
 *
 * The modes are a capability, not a caller's branch: the composable owns the
 * map wiring, the overlay and the arm/reset lifecycle and dispatches each
 * gesture to the active mode's own handlers (the table below). A caller that
 * never names a mode keeps the freehand behaviour it always had, so PoolLasso
 * (multi-region selection) and AddPoolModal (single-pool draw) are unchanged,
 * while the Area filter and the zone draw pass the shared store's mode and get
 * all three. `onCommit(ring, e)` is identical for every mode, which is what
 * lets an existing caller inherit the modes without knowing they exist. A new
 * mode is one entry in that table.
 */

export interface PolygonDrawOptions {
	sourceId: string;
	fillId: string;
	lineId: string;
	fillColor: string;
	fillOpacity: number;
	lineColor: string;
	lineWidth: number;
	/** Committed polygons to render on the overlay (union of all committed). */
	committed: () => GeoJSON.Polygon[];
	/** True while drawing is armed (pointer capture active). */
	armed: Ref<boolean>;
	/** Called with the closed ring when a draw completes with >= 3 points. */
	onCommit: (ring: [number, number][], e: MapMouseEvent) => void;
	/** Called when a draw is cancelled (Escape / modal close). */
	onCancel?: () => void;
	/** Called on armed-state changes (for caller-specific behavior such as
	 * disabling boxZoom or flying to a target). */
	onArmed?: (armed: boolean) => void;
	/** Called for every keydown before Escape handling, so a caller can bind
	 * its own hotkeys (e.g. the lasso's L toggle). Return true to consume. */
	onKey?: (e: KeyboardEvent) => boolean;
	/** The active drawing mode; absent means freehand, so a caller that never
	 * mentions a mode keeps the behaviour it always had. */
	mode?: Ref<DrawMode>;
	/** Circle mode's radius in metres from the tool form; a drag overrides it
	 * while drawing and reports back through `onRadius`. */
	radiusMeters?: Ref<number>;
	/** Circle mode: the radius the draw resolved to (drag distance, or the
	 * form's value for a click). */
	onRadius?: (meters: number) => void;
}

/**
 * The live state of one draw. Every arm, mode switch, cancel and commit clears
 * it, so no mode ever inherits the last one's leftovers.
 */
interface DrawState {
	/** Freehand: the sampled drag points. Polygon: the clicked vertices. */
	points: [number, number][];
	/** Circle: the centre the mousedown set. */
	center: [number, number] | null;
	/** Circle: the radius the drag has sized so far, in metres. */
	radius: number;
	/** Circle: true once the pointer has moved off the centre, so a plain click
	 * can be told from a drag and fall back to the form's own radius. */
	moved: boolean;
}

/** Finish the current draw with a closed ring. */
type Complete = (ring: [number, number][], e: MapMouseEvent) => void;

/** The keyboard's own way to finish: a key has no pointer event of its own. */
type FinishFromKeyboard = (ring: [number, number][]) => void;

/**
 * One drawing mode's behaviour. A mode implements only the events it reads and
 * returns the geometry it wants drawn in progress; the composable owns the
 * plumbing around it. Adding a mode is one entry in the table — nothing in the
 * composable's own handlers changes.
 */
interface ModeHandler {
	/** True when the mode draws with a pointer drag (freehand, circle). False
	 * for a mode that only accumulates clicks, for which the drag lifecycle is
	 * never started. */
	drag: boolean;
	/** Called when a draw arms in this mode, and (as `release`) when it ends or
	 * the mode changes, so a mode can take and give back map behaviour it
	 * needs. */
	arm?(map: Map): void;
	release?(map: Map): void;
	mouseDown?(s: DrawState, e: MapMouseEvent, complete: Complete): void;
	mouseMove?(s: DrawState, e: MapMouseEvent): void;
	mouseUp?(s: DrawState, e: MapMouseEvent, complete: Complete): void;
	/** A click: MapLibre reports none after a drag past its click tolerance. */
	click?(s: DrawState, e: MapMouseEvent, complete: Complete): void;
	/** The closing double-click of a clicked-vertex path. */
	doubleClick?(s: DrawState, e: MapMouseEvent, complete: Complete): void;
	/** A keydown that is not the shared Escape; true when it consumed the key. */
	keyDown?(s: DrawState, e: KeyboardEvent, finish: FinishFromKeyboard): boolean;
	/** The in-progress geometry, drawn over the committed polygons. */
	preview(s: DrawState): GeoJSON.Feature[];
}

/** An open LineString feature through the points a mode is building. */
function lineFeature(points: [number, number][]): GeoJSON.Feature {
	return {
		type: "Feature",
		properties: {},
		geometry: {
			type: "LineString",
			coordinates: points.map((p): [number, number] => [p[0], p[1]]),
		},
	};
}

/** A closed polygon feature for a ring a mode is previewing. */
function polygonFeature(ring: [number, number][]): GeoJSON.Feature {
	return {
		type: "Feature",
		properties: {},
		geometry: { type: "Polygon", coordinates: [[...ring]] },
	};
}

/**
 * Ground distance between two lng/lat pairs in metres, on the sphere
 * @/map/drawModes samples a circle on: a circle's radius is stated in metres,
 * so the drag that sizes it has to measure in metres too.
 */
function groundDistanceMeters(
	a: [number, number],
	b: [number, number],
): number {
	const earthRadiusM = 6371008.8;
	const toRad = (deg: number) => (deg * Math.PI) / 180;
	const dLat = toRad(b[1] - a[1]);
	const dLng = toRad(b[0] - a[0]);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
	return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function usePolygonDraw(opts: PolygonDrawOptions) {
	const mapInstance = useMap();
	const drawing = ref(false);
	// The one array the state's points live in, and the one the composable
	// returns: callers clear it in place to drop an in-progress draw.
	const state: DrawState = {
		points: [],
		center: null,
		radius: 0,
		moved: false,
	};

	// The mode in force: a caller that names none draws freehand.
	const mode = computed<DrawMode>(() => opts.mode?.value ?? "freehand");

	// The mode whose `arm` hook is currently in force, so a disarm or a mode
	// switch gives back exactly the map behaviour that mode took, once.
	let armedMode: DrawMode | null = null;

	// The last pointer position the map reported, which is what a keyboard
	// close commits with: Enter has no pointer event of its own, and only a
	// clicked-vertex draw (which always had one) can close from the keyboard.
	let lastPointer: MapMouseEvent | null = null;

	// The armed state last handed to the caller, so the caller hears a change
	// once — a mode switch re-applies the map's own behaviour without
	// re-reporting an armed state that did not move.
	let reportedArmed: boolean | null = null;

	/** The radius a circle draw resolves to: the drag's own distance when the
	 * pointer moved, else the form's value (0 when the form states none, which
	 * commits nothing). */
	function resolvedRadius(): number {
		return state.moved ? state.radius : (opts.radiusMeters?.value ?? 0);
	}

	function resetState() {
		drawing.value = false;
		state.points.length = 0;
		state.center = null;
		state.radius = 0;
		state.moved = false;
	}

	/** Finish the draw: the mode builds the ring, the composable clears up
	 * after it and hands it on. */
	function complete(ring: [number, number][], e: MapMouseEvent) {
		resetState();
		updateOverlay();
		opts.onCommit(ring, e);
	}

	/** A key gesture's finish, carrying the pointer position that closing the
	 * path means. */
	function finishFromKeyboard(ring: [number, number][]) {
		if (lastPointer) complete(ring, lastPointer);
	}

	const MODE_HANDLERS: Record<DrawMode, ModeHandler> = {
		freehand: {
			drag: true,
			mouseDown(s, e) {
				s.points.push([e.lngLat.lng, e.lngLat.lat]);
			},
			mouseMove(s, e) {
				s.points.push([e.lngLat.lng, e.lngLat.lat]);
			},
			mouseUp(s, e, finish) {
				// Fewer than three sampled points is an accidental click, not a
				// polygon; drop it rather than commit a sliver.
				if (s.points.length < 3) {
					s.points.length = 0;
					return;
				}
				finish([...s.points, s.points[0]], e);
			},
			preview(s) {
				const features: GeoJSON.Feature[] = [];
				if (s.points.length >= 2) features.push(lineFeature(s.points));
				if (s.points.length >= 3) features.push(polygonFeature(s.points));
				return features;
			},
		},
		circle: {
			drag: true,
			mouseDown(s, e) {
				s.center = [e.lngLat.lng, e.lngLat.lat];
				s.radius = 0;
				s.moved = false;
			},
			mouseMove(s, e) {
				if (!s.center) return;
				s.radius = groundDistanceMeters(s.center, [e.lngLat.lng, e.lngLat.lat]);
				s.moved = true;
			},
			mouseUp(s, e, finish) {
				if (!s.center) return;
				const radius = resolvedRadius();
				// A radius of zero draws nothing, so the click is ignored rather
				// than committed as a degenerate point.
				if (radius <= 0) return;
				// The resolved radius goes back to the form, which keeps it for
				// the next circle; the ring uses the same radius, so what the
				// operator sized is what the ring and the server get.
				opts.onRadius?.(radius);
				finish(circleRing(s.center, radius), e);
			},
			preview(s) {
				if (!s.center) return [];
				const radius = resolvedRadius();
				if (radius <= 0) return [];
				return [polygonFeature(circleRing(s.center, radius))];
			},
		},
		polygon: {
			drag: false,
			arm(map) {
				// The closing double-click must not also zoom the map.
				map.doubleClickZoom.disable();
			},
			release(map) {
				map.doubleClickZoom.enable();
			},
			click(s, e) {
				// MapLibre reports a click per half of a double-click; the
				// second is the closing gesture's, so it must not leave a
				// duplicate vertex behind.
				if (e.originalEvent.detail > 1) return;
				moveLayersToTop();
				s.points.push([e.lngLat.lng, e.lngLat.lat]);
			},
			doubleClick(s, e, finish) {
				const polygon = vertexPolygon(s.points);
				if (!polygon) return;
				finish(polygon.coordinates[0] as [number, number][], e);
			},
			keyDown(s, e, finish) {
				if (e.key === "Backspace") {
					// Undo the last vertex; the draw keeps running.
					e.preventDefault();
					s.points.pop();
					return true;
				}
				if (e.key === "Enter") {
					e.preventDefault();
					const polygon = vertexPolygon(s.points);
					if (polygon) {
						finish(polygon.coordinates[0] as [number, number][]);
					}
					return true;
				}
				return false;
			},
			preview(s) {
				// The open path: a clicked-vertex ring is a line until it closes.
				return s.points.length >= 2 ? [lineFeature(s.points)] : [];
			},
		},
	};

	let map: Map | null = null;
	// True only once the map's style is loaded and the overlay source/layers
	// exist. It must NOT be set when the map instance is merely captured: the
	// immediate `armed` watcher below runs during setup and calls
	// updateOverlay(), whose getSource() would throw on a map whose style is
	// still undefined (MapLibre: reading 'getSource' of undefined).
	let initialized = false;

	function init(m: Map) {
		m.addSource(opts.sourceId, {
			type: "geojson",
			data: { type: "FeatureCollection", features: [] },
		});
		m.addLayer({
			id: opts.fillId,
			type: "fill",
			source: opts.sourceId,
			paint: { "fill-color": opts.fillColor, "fill-opacity": opts.fillOpacity },
		});
		m.addLayer({
			id: opts.lineId,
			type: "line",
			source: opts.sourceId,
			paint: {
				"line-color": opts.lineColor,
				"line-width": opts.lineWidth,
				"line-dasharray": [2, 1],
			},
		});
		m.on("mousedown", onMouseDown);
		m.on("mousemove", onMouseMove);
		m.on("mouseup", onMouseUp);
		m.on("click", onClick);
		m.on("dblclick", onDoubleClick);
		window.addEventListener("keydown", onKeyDown);
		initialized = true;
		updateOverlay();
	}

	function onMouseDown(e: MapMouseEvent) {
		if (!opts.armed.value || drawing.value) return;
		if (e.originalEvent.button !== 0) return;
		const handler = MODE_HANDLERS[mode.value];
		if (!handler.drag) return;
		drawing.value = true;
		// A drag starts a fresh shape: the last one was either committed (which
		// cleared the state) or too small to be one (which did not).
		state.points.length = 0;
		// Guard against imagery/feature layers covering the draw.
		moveLayersToTop();
		handler.mouseDown?.(state, e, complete);
		updateOverlay();
	}

	function onMouseMove(e: MapMouseEvent) {
		lastPointer = e;
		if (!drawing.value) return;
		MODE_HANDLERS[mode.value].mouseMove?.(state, e);
		updateOverlay();
	}

	function onMouseUp(e: MapMouseEvent) {
		if (!drawing.value) return;
		drawing.value = false;
		MODE_HANDLERS[mode.value].mouseUp?.(state, e, complete);
		updateOverlay();
	}

	function onClick(e: MapMouseEvent) {
		if (!opts.armed.value) return;
		const handler = MODE_HANDLERS[mode.value];
		if (handler.drag || !handler.click) return;
		handler.click(state, e, complete);
		updateOverlay();
	}

	function onDoubleClick(e: MapMouseEvent) {
		if (!opts.armed.value) return;
		const handler = MODE_HANDLERS[mode.value];
		if (handler.drag || !handler.doubleClick) return;
		handler.doubleClick(state, e, complete);
		updateOverlay();
	}

	function onKeyDown(e: KeyboardEvent) {
		const tag = (e.target as HTMLElement)?.tagName;
		if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
		if (opts.onKey && opts.onKey(e)) return;
		if (!opts.armed.value) return;
		if (e.key === "Escape") {
			cancel();
			return;
		}
		if (MODE_HANDLERS[mode.value].keyDown?.(state, e, finishFromKeyboard)) {
			updateOverlay();
		}
	}

	/** Render the committed polygons plus whatever the active mode is drawing. */
	function updateOverlay() {
		if (!map || !initialized) return;
		const src = map.getSource(opts.sourceId) as GeoJSONSource | undefined;
		if (!src) return;
		const features: GeoJSON.Feature[] = opts.committed().map((polygon) => ({
			type: "Feature",
			properties: {},
			geometry: polygon,
		}));
		features.push(...MODE_HANDLERS[mode.value].preview(state));
		src.setData({ type: "FeatureCollection", features });
	}

	/** Raise the draw layers above every other layer on the map. */
	function moveLayersToTop() {
		if (!map || !initialized) return;
		for (const id of [opts.fillId, opts.lineId]) {
			if (map.getLayer(id)) map.moveLayer(id);
		}
	}

	function cancel() {
		resetState();
		updateOverlay();
		opts.onCancel?.();
	}

	/**
	 * Apply the armed state to the map: the mode's own behaviour (the
	 * clicked-vertex mode takes the double-click zoom) plus the pan/cursor
	 * handling every draw needs, because drawing needs a drag and not a pan.
	 * Runs on every arm change and on every mode change, and again once the map
	 * arrives if it was not there when the state was first applied.
	 */
	function syncArmed() {
		// Bound out of the table first: TypeScript cannot narrow an optional
		// method read through an index-signature access in the condition.
		const previousRelease = armedMode
			? MODE_HANDLERS[armedMode].release
			: undefined;
		if (map && previousRelease) previousRelease(map);
		armedMode = null;
		resetState();
		if (map && opts.armed.value) {
			map.dragPan.disable();
			// Shift+drag draws a zoom box in MapLibre by default — turn that off
			// while drawing so Shift+drag can draw/append instead.
			map.boxZoom.disable();
			map.getCanvas().style.cursor = "crosshair";
			moveLayersToTop();
			MODE_HANDLERS[mode.value].arm?.(map);
			armedMode = mode.value;
		} else if (map) {
			map.dragPan.enable();
			map.boxZoom.enable();
			map.getCanvas().style.cursor = "";
		}
		updateOverlay();
		if (reportedArmed !== opts.armed.value) {
			reportedArmed = opts.armed.value;
			opts.onArmed?.(opts.armed.value);
		}
	}

	watch([opts.armed, mode], syncArmed, { immediate: true });

	watch(
		() => mapInstance.map,
		(m) => {
			if (m && !map) {
				map = m;
				if (m.loaded()) init(m);
				else m.once("load", () => init(m));
				// The map has only just arrived, so the armed state applied
				// before it was captured has to be applied to it now.
				syncArmed();
			}
		},
		{ immediate: true },
	);

	onBeforeUnmount(() => {
		if (map && map.style) {
			map.off("mousedown", onMouseDown);
			map.off("mousemove", onMouseMove);
			map.off("mouseup", onMouseUp);
			map.off("click", onClick);
			map.off("dblclick", onDoubleClick);
			if (map.getLayer(opts.fillId)) map.removeLayer(opts.fillId);
			if (map.getLayer(opts.lineId)) map.removeLayer(opts.lineId);
			if (map.getSource(opts.sourceId)) map.removeSource(opts.sourceId);
			map.dragPan.enable();
			map.getCanvas().style.cursor = "";
		}
		window.removeEventListener("keydown", onKeyDown);
	});

	return {
		drawing,
		points: state.points,
		updateOverlay,
		moveLayersToTop,
		cancel,
		/** The resolved Map instance once the map has loaded (or null). */
		getMap: () => map,
	};
}
